import React from 'react';

interface Bid {
  amount: number;
  created_at: string;
  status?: string;
}

interface BidHistoryGraphProps {
  bids: Bid[];
  height?: number;
  showAxis?: boolean;
}

export function BidHistoryGraph({ bids, height = 80, showAxis = false }: BidHistoryGraphProps) {
  // Ensure we have bids to show
  const activeBids = bids?.filter(b => b.status !== 'withdrawn') || [];
  if (activeBids.length === 0) return null;
  
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(0);

  React.useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.clientWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Sort bids by creation time
  const sortedBids = [...activeBids].sort((a, b) =>
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  // Find min and max values for scaling
  const amounts = sortedBids.map(b => b.amount);
  const minAmount = Math.min(...amounts);
  const maxAmount = Math.max(...amounts);
  
  // Scale Y-axis from 0 to the highest bid rounded to nearest 100
  const yAxisMin = 0; 
  const yAxisMax = Math.ceil(maxAmount / 100) * 100;
  const range = yAxisMax - yAxisMin || 1; // Prevent division by zero

  // Graph padding
  const padding = 10;
  const graphHeight = height - (padding * 2);
  const graphWidth = width - (padding * 2);

  // Bar width (no spacing)
  const barWidth = graphWidth / sortedBids.length;
  const spacing = 0;

  // Colors from the logo
  const colors = ['#9336e9', '#1254e6', '#f9c708', '#ee3c3a', '#25d3bc', '#60a4f9'];

  // Calculate bar heights
  const getBarHeight = (amount: number) => {
    return ((amount - yAxisMin) / range) * graphHeight;
  };

  // Generate scale markers
  const scaleMarkers = [];
  const numMarkers = 5;
  for (let i = 0; i <= numMarkers; i++) {
    const value = (yAxisMax * (i / numMarkers));
    scaleMarkers.push(value);
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        width={width}
        height={height}
        className="max-w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Axis lines */}
        {showAxis && (
          <g transform={`translate(${padding}, ${padding})`}>
            {/* Horizontal axis */}
            {scaleMarkers.map((value, i) => (
              <g key={`scale-${i}`}>
                <line
                  x1={0}
                  y1={graphHeight - (graphHeight * (i / numMarkers))}
                  x2={graphWidth}
                  y2={graphHeight - (graphHeight * (i / numMarkers))}
                  stroke="#e5e7eb"
                  strokeWidth={0.5}
                  strokeDasharray={i === 0 ? "none" : "4,4"}
                />
                <text
                  x={-5}
                  y={graphHeight - (graphHeight * (i / numMarkers))}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="text-[10px] fill-gray-500"
                >
                  £{Math.round(value).toLocaleString()}
                </text>
              </g>
            ))}
          </g>
        )}

        {/* Bars */}
        {sortedBids.map((bid, index) => {
          const x = padding + (index * (barWidth + spacing));
          const barHeight = getBarHeight(bid.amount);
          const dateLabel = new Date(bid.created_at).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit'
          });
          
          return (
            <g key={`${bid.created_at}-${index}`}>
              <rect
                x={x}
                y={height - barHeight - padding}
                width={barWidth}
                height={barHeight}
                fill={colors[index % colors.length]}
                rx={2}
                className="transition-all duration-300"
              >
                <title>
                  £{bid.amount.toLocaleString('en-GB', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                  {'\n'}
                  {new Date(bid.created_at).toLocaleString()}
                </title>
              </rect>
              {showAxis && (
                <text
                  x={x + (barWidth / 2)}
                  y={height - 2}
                  textAnchor="middle"
                  className="text-[10px] fill-gray-500"
                >
                  {dateLabel}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}